/**
 * Utility functions for formatting and validation
 */

/**
 * Validates a Kenyan phone number
 * @param {string} phone - The phone number to validate
 * @returns {boolean} - Returns true if the phone number is valid
 */
export function isPhone(phone) {
  if (!phone || typeof phone !== 'string') {
    return false;
  }

  // Remove any whitespace and special characters
  const cleanedPhone = phone.replace(/\s+/g, '').replace(/[^\d]/g, '');

  // Check for Kenyan phone number patterns:
  // 07XXXXXXXX, 01XXXXXXXX (8 digits after prefix)
  // +2547XXXXXXXX, +2541XXXXXXXX (9 digits after prefix)
  const phoneRegex = /^(?:07|01|2547|2541)\d{8}$/;
  return phoneRegex.test(cleanedPhone);
}

/**
 * Formats a phone number to a standard format
 * @param {string} phone - The phone number to format
 * @returns {string} - Formatted phone number or empty string if invalid
 */
export function formatPhone(phone) {
  if (!isPhone(phone)) {
    return '';
  }

  const cleanedPhone = phone.replace(/\s+/g, '').replace(/[^\d]/g, '');
  
  // If number starts with 254, keep it as is
  if (cleanedPhone.startsWith('254')) {
    return `+${cleanedPhone}`;
  }
  
  // If number starts with 07 or 01, convert to 254 format
  if (cleanedPhone.startsWith('07') || cleanedPhone.startsWith('01')) {
    return `+254${cleanedPhone.slice(1)}`;
  }
  
  return '';
}

/**
 * Formats a number to currency format
 * @param {number} amount - The amount to format
 * @param {string} currency - The currency symbol (default: 'KSh')
 * @returns {string} - Formatted currency string
 */
export function formatCurrency(amount, currency = 'KSh') {
  if (typeof amount !== 'number' || isNaN(amount)) {
    return '';
  }
  
  return `${currency} ${amount.toLocaleString('en-KE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
}

/**
 * Formats a date to a readable string
 * @param {Date|string} date - The date to format
 * @returns {string} - Formatted date string
 */
export function formatDate(date) {
  if (!date) {
    return '';
  }
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    return '';
  }
  
  return dateObj.toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

const PLUS_CODE_ALPHABET = '23456789CFGHJMPQRVWX';
const PLUS_CODE_BASE = PLUS_CODE_ALPHABET.length;
const PLUS_CODE_LATITUDE_MAX = 90;
const PLUS_CODE_LONGITUDE_MAX = 180;
const PLUS_CODE_PAIR_RESOLUTIONS = [20.0, 1.0, 0.05, 0.0025, 0.000125];
const PLUS_CODE_SEPARATOR = '+';
const PLUS_CODE_SEPARATOR_POSITION = 8;
const PLUS_CODE_LENGTH = 10;

/**
 * Checks whether a location value holds raw Latitude/Longitude coordinates
 * (a { latitude, longitude } object or a "lat,lng" string) rather than an
 * already human-readable address or Plus Code.
 * @param {any} location - The location value to inspect
 * @returns {boolean} - Returns true if the location holds coordinates
 */
export function isLatLngCoordinates(location) {
  if (!location) {
    return false;
  }

  if (typeof location === 'object') {
    return typeof location.latitude === 'number' && typeof location.longitude === 'number';
  }

  if (typeof location === 'string') {
    const coordPattern = /^-?\d{1,3}(\.\d+)?\s*,\s*-?\d{1,3}(\.\d+)?$/;
    return coordPattern.test(location.trim());
  }

  return false;
}

/**
 * Encodes Latitude/Longitude coordinates into a full-precision Open
 * Location Code (Plus Code), e.g. "6GCRPR6C+2X".
 * @param {number} latitude - Latitude in degrees
 * @param {number} longitude - Longitude in degrees
 * @returns {string} - The encoded Plus Code, or empty string if invalid
 */
export function toPlusCode(latitude, longitude) {
  if (typeof latitude !== 'number' || typeof longitude !== 'number' ||
      isNaN(latitude) || isNaN(longitude)) {
    return '';
  }

  let clippedLatitude = Math.min(Math.max(latitude, -PLUS_CODE_LATITUDE_MAX), PLUS_CODE_LATITUDE_MAX);
  let normalizedLongitude = longitude;
  while (normalizedLongitude < -PLUS_CODE_LONGITUDE_MAX) normalizedLongitude += 360;
  while (normalizedLongitude >= PLUS_CODE_LONGITUDE_MAX) normalizedLongitude -= 360;

  // Nudge off the pole so the encoder doesn't overflow into an 11th digit
  if (clippedLatitude === PLUS_CODE_LATITUDE_MAX) {
    clippedLatitude -= Math.pow(PLUS_CODE_BASE, Math.floor(PLUS_CODE_LENGTH / -2 + 2));
  }

  let adjustedLatitude = clippedLatitude + PLUS_CODE_LATITUDE_MAX;
  let adjustedLongitude = normalizedLongitude + PLUS_CODE_LONGITUDE_MAX;

  let code = '';
  let digitCount = 0;
  while (digitCount < PLUS_CODE_LENGTH) {
    const placeValue = PLUS_CODE_PAIR_RESOLUTIONS[Math.floor(digitCount / 2)];

    let digitValue = Math.floor(adjustedLatitude / placeValue);
    adjustedLatitude -= digitValue * placeValue;
    code += PLUS_CODE_ALPHABET.charAt(digitValue);
    digitCount += 1;

    digitValue = Math.floor(adjustedLongitude / placeValue);
    adjustedLongitude -= digitValue * placeValue;
    code += PLUS_CODE_ALPHABET.charAt(digitValue);
    digitCount += 1;

    if (digitCount === PLUS_CODE_SEPARATOR_POSITION && digitCount < PLUS_CODE_LENGTH) {
      code += PLUS_CODE_SEPARATOR;
    }
  }

  return code;
}

/**
 * Formats a Firebase timestamp to YYYY-MM-DD format
 * @param {any} timestamp - The Firebase timestamp (could be string, number, or object)
 * @returns {string} - Formatted date string in YYYY-MM-DD format
 */
export function formatFirebaseDate(timestamp) {
  if (!timestamp) {
    return '';
  }
  
  let date;
  
  // Handle different Firebase timestamp formats
  if (typeof timestamp === 'string') {
    // Handle Firebase server timestamp string format like "August 14, 2025 at 10:12:39 PM UTC+3"
    if (timestamp.includes('at') && timestamp.includes('UTC')) {
      // Extract the date part before "at"
      const datePart = timestamp.split(' at ')[0];
      date = new Date(datePart);
    } else {
      date = new Date(timestamp);
    }
  } else if (typeof timestamp === 'number') {
    // Handle Firebase timestamp as milliseconds
    date = new Date(timestamp);
  } else if (timestamp && typeof timestamp === 'object') {
    // Handle Firebase Timestamp object
    if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else if (timestamp.toDate) {
      date = timestamp.toDate();
    } else {
      date = new Date(timestamp);
    }
  } else {
    date = new Date(timestamp);
  }
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return '';
  }
  
  // Format to YYYY-MM-DD
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

/**
 * Formats a Firebase timestamp with date and time information
 * @param {any} timestamp - The Firebase timestamp (could be string, number, or object)
 * @param {boolean} includeTime - Whether to include time in the output (default: false)
 * @returns {string} - Formatted date string
 */
export function formatFirebaseDateTime(timestamp, includeTime = false) {
  if (!timestamp) {
    return '';
  }
  
  let date;
  
  // Handle different Firebase timestamp formats
  if (typeof timestamp === 'string') {
    // Handle Firebase server timestamp string format like "August 14, 2025 at 10:12:39 PM UTC+3"
    if (timestamp.includes('at') && timestamp.includes('UTC')) {
      // Extract the date part before "at"
      const datePart = timestamp.split(' at ')[0];
      date = new Date(datePart);
    } else {
      date = new Date(timestamp);
    }
  } else if (typeof timestamp === 'number') {
    // Handle Firebase timestamp as milliseconds
    date = new Date(timestamp);
  } else if (timestamp && typeof timestamp === 'object') {
    // Handle Firebase Timestamp object
    if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else if (timestamp.toDate) {
      date = timestamp.toDate();
    } else {
      date = new Date(timestamp);
    }
  } else {
    date = new Date(timestamp);
  }
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return '';
  }
  
  if (includeTime) {
    // Format with date and time
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } else {
    // Format to YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  }
}
